<template>
  <a-modal title="Adjust Balance" :visible="visible" destroy-on-close @cancel="close">
    <p class="adjustment-modal__description">
      Add or deduct hours. The server records who made the change and when; the user does not choose an effective date.
    </p>

    <a-form-model ref="formRef" :model="form" :rules="rules" layout="vertical">
      <div class="adjustment-modal__fields">
        <a-form-model-item label="Category" prop="category">
          <custom-select
            :value.sync="form.category"
            :allow-clear="false"
            :disabled="loading"
            :options="categoryOptions"
            name="label"
            option-key="value"
            @change="handleCategoryChange"
          />
        </a-form-model-item>

        <a-form-model-item label="Action" prop="action">
          <custom-select
            :value.sync="form.action"
            :allow-clear="false"
            :disabled="loading"
            :options="actionOptions"
            name="label"
            option-key="value"
            @change="handleActionChange"
          />
        </a-form-model-item>

        <a-form-model-item class="adjustment-modal__wide-field" label="Hours" prop="amount">
          <a-input-number
            v-model="form.amount"
            :disabled="loading"
            :min="0"
            :precision="4"
            :step="0.25"
            @change="clearAmountValidation"
          />
        </a-form-model-item>

        <a-form-model-item class="adjustment-modal__wide-field" label="Reason" prop="reason">
          <a-textarea v-model="form.reason" :disabled="loading" :rows="2" :max-length="2048" placeholder="Required" />
        </a-form-model-item>
      </div>
    </a-form-model>

    <a-alert v-if="selectedBalance && amountIsPositive" class="adjustment-modal__preview" type="success" show-icon>
      <template #message>
        {{ categoryLabel(form.category) }} balance: {{ formatHours(selectedBalance.current) }} →
        {{ formatHours(nextBalance) }}
      </template>
    </a-alert>

    <template slot="footer">
      <div class="flex justify-content-between">
        <a-button @click="close">Cancel</a-button>
        <a-button icon="save" type="primary" :disabled="!canSubmit" @click="submit" :loading="loading"
          >{{ submitLabel }}
        </a-button>
      </div>
    </template>
  </a-modal>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import { Alert, FormModel, Input, InputNumber, Modal } from 'ant-design-vue'
import { createNamespacedHelpers } from 'vuex'
import CustomSelect from '@/components/ui/custom-select.vue'
import util from '@/util'

const { mapActions } = createNamespacedHelpers('accrual')

const CATEGORY_LABELS = {
  sick: 'Sick',
  vacation: 'Vacation',
  time_off: 'Time Off'
}

const ACTION_OPTIONS = [
  { value: 'add', label: 'Add hours' },
  { value: 'deduct', label: 'Deduct hours' }
]

const initialForm = () => ({
  category: undefined,
  action: 'add',
  amount: undefined,
  reason: ''
})

const decimalToUnits = value => {
  const normalized = String(value ?? '').trim()

  if (!/^\d+(\.\d{1,4})?$/.test(normalized)) return null

  const [whole, fraction = ''] = normalized.split('.')

  return Number(whole) * 10000 + Number(fraction.padEnd(4, '0'))
}

export default {
  components: {
    'a-alert': Alert,
    'a-form-model': FormModel,
    'a-form-model-item': FormModel.Item,
    'a-input-number': InputNumber,
    'a-modal': Modal,
    'a-textarea': Input.TextArea,
    'custom-select': CustomSelect
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    associateId: {
      type: [Number, String],
      required: true
    },
    isUnion: {
      type: Boolean,
      default: null
    },
    balances: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      form: initialForm(),
      idempotencyKey: uuidv4(),
      loading: false,
      rules: {
        category: [{ required: true, message: 'Select a category.', trigger: 'change' }],
        amount: [{ validator: this.validateAmount, trigger: 'change' }],
        reason: [{ required: true, whitespace: true, message: 'Reason is required.', trigger: 'change' }]
      }
    }
  },
  computed: {
    categoryOptions() {
      const allowedCategories = this.isUnion === true ? ['sick', 'vacation'] : this.isUnion === false ? ['time_off'] : []

      return allowedCategories.map(category => ({ value: category, label: this.categoryLabel(category) }))
    },
    actionOptions() {
      return ACTION_OPTIONS
    },
    selectedBalance() {
      if (!this.form.category) return undefined

      return this.balances.find(balance => balance.category === this.form.category) ?? {
        category: this.form.category,
        current: '0.0000',
        reserved: '0.0000',
        available: '0.0000'
      }
    },
    amountUnits() {
      return decimalToUnits(this.form.amount)
    },
    amountIsPositive() {
      return this.amountUnits !== null && this.amountUnits > 0
    },
    currentUnits() {
      return decimalToUnits(this.selectedBalance?.current) ?? 0
    },
    nextBalance() {
      if (!this.selectedBalance || !this.amountIsPositive) return this.selectedBalance?.current

      const nextUnits =
        this.form.action === 'deduct' ? this.currentUnits - this.amountUnits : this.currentUnits + this.amountUnits

      return nextUnits / 10000
    },
    canSubmit() {
      return (
        !!this.selectedBalance &&
        this.amountIsPositive &&
        !!this.form.reason.trim() &&
        !(this.form.action === 'deduct' && this.amountUnits > this.currentUnits)
      )
    },
    submitLabel() {
      const verb = this.form.action === 'deduct' ? 'Deduct' : 'Add'

      return this.amountIsPositive ? `${verb} ${this.formatHours(this.form.amount)}` : `${verb} Hours`
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.resetForm()
      }
    },
    isUnion() {
      if (this.visible) this.resetForm()
    },
    form: {
      handler() {
        if (!this.loading) this.idempotencyKey = uuidv4()
      },
      deep: true
    }
  },
  methods: {
    ...mapActions(['adjustAccrualBalance']),
    categoryLabel(category) {
      return CATEGORY_LABELS[category] ?? category
    },
    formatHours(value) {
      const numericValue = Number(value ?? 0)

      return util.formatMinutesToHoursAndMinutesDuration(Number.isFinite(numericValue) ? Math.round(numericValue * 60) : 0)
    },
    resetForm() {
      this.form = {
        ...initialForm(),
        category: this.categoryOptions[0]?.value
      }
      this.$nextTick(() => this.$refs.formRef?.clearValidate())
    },
    close() {
      if (this.loading) return

      this.$emit('update:visible', false)
    },
    handleCategoryChange() {
      this.$nextTick(() => this.$refs.formRef?.clearValidate(['amount']))
    },
    handleActionChange() {
      this.$nextTick(() => this.$refs.formRef?.clearValidate(['amount']))
    },
    clearAmountValidation() {
      this.$nextTick(() => this.$refs.formRef?.validateField('amount'))
    },
    validateAmount(_, value, callback) {
      const units = decimalToUnits(value)

      if (units === null || units <= 0) {
        callback(new Error('Enter hours greater than 0.'))
        return
      }

      if (this.form.action === 'deduct' && units > this.currentUnits) {
        callback(
          new Error(
            `You can deduct up to ${this.formatHours(this.selectedBalance?.current)} ${this.categoryLabel(this.form.category)}.`
          )
        )
        return
      }

      callback()
    },
    async submit() {
      this.$refs.formRef.validate(async valid => {
        if (!valid || !this.canSubmit) return

        this.loading = true

        try {
          await this.adjustAccrualBalance({
            action: this.form.action,
            payload: {
              associate_id: this.associateId,
              category: this.form.category,
              amount: String(this.form.amount),
              reason: this.form.reason.trim(),
              idempotency_key: `profile-accrual-adjustment-${this.idempotencyKey}`
            }
          })
          this.$emit('saved')
          this.$emit('update:visible', false)
        } catch (error) {
          this.$emit('error', error)
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.adjustment-modal__description {
  margin: 0 0 16px;
  color: var(--text-color-secondary);
}

.adjustment-modal__fields {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0 12px;
}

.adjustment-modal__wide-field {
  grid-column: span 2;
}

.adjustment-modal__preview {
  margin-top: 8px;
}

@media (max-width: 520px) {
  .adjustment-modal__fields {
    grid-template-columns: 1fr;
  }

  .adjustment-modal__wide-field {
    grid-column: auto;
  }
}
</style>
